import { useCallback, useContext, useMemo } from 'react';
import AdminContext, { type AdminContextType } from '../context/admin';

export interface TablePreferences {
  sortFieldId?: string | number;
  sortAsc?: boolean;
  rowsPerPage?: number;
}

const STORAGE_PREFIX = 'tablePrefs';

/**
 * Persists per-table UI preferences (sort field/direction, rows per page)
 * to localStorage, scoped by user id + a stable table key.
 *
 * Used by the DataTable molecule via its `persistKey` prop — pass a key
 * that is unique per table, e.g. 'buildings-list'.
 */
export const useTablePreferences = (persistKey?: string) => {
  const { user } = useContext(AdminContext) as AdminContextType;

  const storageKey = persistKey
    ? `${STORAGE_PREFIX}:${user?.id ?? 'anonymous'}:${persistKey}`
    : null;

  // read once per key - table defaults only matter at mount
  const preferences = useMemo<TablePreferences>(() => {
    if (!storageKey) return {};
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as TablePreferences) : {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const savePreferences = useCallback(
    (update: Partial<TablePreferences>) => {
      if (!storageKey) return;
      try {
        const raw = localStorage.getItem(storageKey);
        const current = raw ? (JSON.parse(raw) as TablePreferences) : {};
        localStorage.setItem(
          storageKey,
          JSON.stringify({ ...current, ...update })
        );
      } catch {
        // storage unavailable (private mode, quota) - degrade silently
      }
    },
    [storageKey]
  );

  return { preferences, savePreferences };
};
